import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Mail, Globe } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/3d-concrete-printing', label: t('nav.printing3d') },
    { path: '/modular-platform', label: t('nav.modular') },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') }
  ];

  return (
    <>
      <div className="hidden lg:block bg-neutral-900 text-white py-2 fixed top-0 w-full z-50">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="mailto:modular@wedevelop.one" className="flex items-center gap-2 hover:text-red-400 transition-colors">
              <Mail size={14} />
              modular@wedevelop.one
            </a>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 hover:text-red-400 transition-colors bg-neutral-800 px-3 py-1 rounded-full"
            >
              <Globe size={14} />
              <span className="font-medium">{language === 'ES' ? 'ES' : 'EN'}</span>
              <span className="text-neutral-400 text-xs">|</span>
              <span className="text-neutral-400">{language === 'ES' ? 'EN' : 'ES'}</span>
            </button>
          </div>
        </div>
      </div>

      <nav
        className={`fixed w-full z-40 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg py-3 top-0 lg:top-[36px]'
            : 'bg-transparent py-5 top-0 lg:top-[36px]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-3 group">
              <div className={`text-2xl font-bold tracking-tight transition-colors duration-300 ${
                isScrolled ? 'text-neutral-900' : 'text-white'
              }`}>
                <span className="font-light">We</span>
                <span className="font-black">Develop</span>
              </div>
            </Link>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative text-sm font-medium transition-colors duration-300 group ${
                    isScrolled ? 'text-neutral-700 hover:text-neutral-900' : 'text-white/90 hover:text-white'
                  } ${location.pathname === link.path ? 'font-semibold' : ''}`}
                >
                  {link.label}
                  <span className={`absolute -bottom-1 left-0 h-0.5 bg-red-500 transition-all duration-300 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`} />
                </Link>
              ))}
            </div>

            <div className="hidden lg:flex items-center gap-3">
              <Button
                variant="outline"
                className={`border-2 transition-all duration-300 ${
                  isScrolled
                    ? 'border-neutral-900 text-neutral-900 hover:bg-neutral-900 hover:text-white'
                    : 'border-white text-white hover:bg-white hover:text-neutral-900'
                }`}
                asChild
              >
                <Link to="/contact">{t('nav.scheduleCall')}</Link>
              </Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" asChild>
                <a href="https://wa.me/5491112345678" target="_blank" rel="noopener noreferrer">
                  {t('nav.whatsapp')}
                </a>
              </Button>
            </div>

            <button
              onClick={() => setIsMobileOpen(!isMobileOpen)}
              className={`lg:hidden p-2 transition-colors ${
                isScrolled ? 'text-neutral-900' : 'text-white'
              }`}
            >
              {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        <div
          className={`lg:hidden absolute top-full left-0 w-full bg-white shadow-xl transition-all duration-300 ${
            isMobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
        >
          <div className="p-6 space-y-4">
            {/* Mobile language toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-neutral-900 font-medium pb-4 border-b border-neutral-100 w-full"
            >
              <Globe size={18} />
              {language === 'ES' ? 'English' : 'Español'}
            </button>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileOpen(false)}
                className="block py-3 text-neutral-900 font-medium border-b border-neutral-100"
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 space-y-3">
              <Button className="w-full bg-red-600 hover:bg-red-700" asChild>
                <Link to="/contact">{t('nav.scheduleCall')}</Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
